/**
 * Relatórios Administrativos — redesign moderno
 */

import React, { useState } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Button,
  Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Avatar,
} from '@mui/material';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import DownloadIcon      from '@mui/icons-material/Download';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import PeopleIcon        from '@mui/icons-material/People';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import FilterListIcon    from '@mui/icons-material/FilterList';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

/* ─── palette ─── */
const ORANGE  = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE    = '#2563EB';
const GREEN   = '#10B981';
const RED     = '#EF4444';
const AMBER   = '#F59E0B';

const fmt = (v) =>
  `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── KPI Card ─── */
const KpiCard = ({ title, value, sub, icon: Icon, gradient }) => (
  <Card elevation={0} sx={{
    borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff',
    overflow: 'hidden',
    transition: 'transform 0.18s, box-shadow 0.18s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' },
  }}>
    <Box sx={{ height: 4, background: gradient }} />
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.4 }}>{sub}</Typography>}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(0,0,0,0.15)', flexShrink: 0,
        }}>
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/* ─── TH helper ─── */
const TH = ({ children, align = 'left' }) => (
  <TableCell align={align} sx={{
    fontSize: 10, fontWeight: 700, color: '#94A3B8',
    letterSpacing: '0.07em', textTransform: 'uppercase',
    borderBottom: '2px solid #F1F5F9', pb: 1.2, bgcolor: '#FAFAFA',
  }}>
    {children}
  </TableCell>
);

/* ─── chip tipo ─── */
const TipoChip = ({ tipo }) => {
  const m = {
    PF:      { bg: 'rgba(249,115,22,0.1)', color: ORANGE },
    PJ:      { bg: 'rgba(37,99,235,0.1)',  color: BLUE   },
    HIBRIDO: { bg: 'rgba(245,158,11,0.1)', color: AMBER  },
  };
  const s = m[tipo] || { bg: '#F1F5F9', color: '#64748B' };
  return <Chip label={tipo} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11, height: 22, border: 'none' }} />;
};

/* ─── dados mock ─── */
const MOCK = [
  { id: 1, nome: 'João Silva',    email: 'joao@email.com',  tipo: 'PF',      status: true,  lancamentos: 45, faturamento: 12500, despesas: 4300 },
  { id: 2, nome: 'Maria Santos',  email: 'maria@email.com', tipo: 'PJ',      status: true,  lancamentos: 78, faturamento: 38900, despesas: 11200 },
  { id: 3, nome: 'Pedro Costa',   email: 'pedro@email.com', tipo: 'HIBRIDO', status: true,  lancamentos: 32, faturamento: 9700,  despesas: 2800 },
];

const TIPOS_RELATORIO = [
  { value: 'usuarios',    label: 'Usuários'    },
  { value: 'lancamentos', label: 'Lançamentos' },
  { value: 'financeiro',  label: 'Financeiro'  },
  { value: 'analises',    label: 'Análises'    },
];

const PERIODOS = [
  { value: 'hoje',      label: 'Hoje'           },
  { value: 'semana',    label: 'Esta semana'    },
  { value: 'mes',       label: 'Este mês'       },
  { value: 'trimestre', label: 'Este trimestre' },
  { value: 'ano',       label: 'Este ano'       },
];

/* ═══════════════════════════════════════════ */
const RelatoriosAdmin = () => {
  const [tipo,   setTipo]   = useState('usuarios');
  const [periodo, setPeriodo] = useState('mes');

  const tipoLabel   = TIPOS_RELATORIO.find(t => t.value === tipo)?.label   || '';
  const periodoLabel = PERIODOS.find(p => p.value === periodo)?.label || '';

  const totalFat  = MOCK.reduce((s, r) => s + r.faturamento, 0);
  const totalDesp = MOCK.reduce((s, r) => s + r.despesas,    0);
  const totalLanc = MOCK.reduce((s, r) => s + r.lancamentos, 0);

  return (
    <Box sx={{ pb: 4 }}>

      {/* ── Hero ── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.07)' }} />
        <Box sx={{ position: 'absolute', right: 120, bottom: -50, width: 110, height: 110, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.06)' }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 0.3 }}>
              Relatórios Administrativos
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)' }}>
              Gere, visualize e exporte relatórios consolidados da plataforma
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
              {[
                { Icon: PeopleIcon,      label: 'Usuários',      val: MOCK.length },
                { Icon: ReceiptLongIcon, label: 'Lançamentos',   val: totalLanc   },
                { Icon: TrendingUpIcon,  label: 'Faturamento',   val: fmt(totalFat)  },
              ].map(({ Icon, label, val }) => (
                <Box key={label} sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 0.9,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 2,
                }}>
                  <Icon sx={{ fontSize: 16, color: ORANGE_L }} />
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'rgba(147,197,253,0.7)', lineHeight: 1 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{val}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* export buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
              onClick={() => alert('Exportando PDF...')}
              sx={{
                borderRadius: 2, fontSize: 13, fontWeight: 700, textTransform: 'none',
                background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                boxShadow: '0 3px 10px rgba(249,115,22,0.4)', py: 1, px: 2.5,
                '&:hover': { boxShadow: '0 4px 16px rgba(249,115,22,0.5)' },
              }}
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => alert('Exportando Excel...')}
              sx={{
                borderRadius: 2, fontSize: 13, fontWeight: 600, textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.3)', color: '#fff', py: 1, px: 2.5,
                '&:hover': { borderColor: '#60A5FA', bgcolor: 'rgba(37,99,235,0.12)' },
              }}
            >
              Excel
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard title="Total Usuários"   value={MOCK.length}   sub="No período selecionado"   icon={PeopleIcon}               gradient={`linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard title="Faturamento Total" value={fmt(totalFat)} sub="Soma de todos os usuários" icon={TrendingUpIcon}            gradient={`linear-gradient(135deg, ${GREEN} 0%, #34D399 100%)`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard title="Despesas Totais"  value={fmt(totalDesp)} sub="Soma de todos os usuários" icon={TrendingDownIcon}          gradient={`linear-gradient(135deg, ${RED} 0%, #F87171 100%)`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard title="Lançamentos"      value={totalLanc}     sub="Faturam. + Despesas"        icon={AccountBalanceWalletIcon}  gradient={`linear-gradient(135deg, ${BLUE} 0%, #60A5FA 100%)`} />
        </Grid>
      </Grid>

      {/* ── Filtros + Tabela ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', overflow: 'hidden' }}>

        {/* barra de filtros */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          px: 3, py: 2,
          borderBottom: '1px solid #F1F5F9',
          flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <FilterListIcon sx={{ fontSize: 17, color: ORANGE }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>Filtros</Typography>
          </Box>

          <Select
            size="small"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            renderValue={(v) => {
              const l = TIPOS_RELATORIO.find(t => t.value === v)?.label;
              return <Typography sx={{ fontSize: 12.5, color: '#1E293B' }}>Tipo: <b>{l}</b></Typography>;
            }}
            sx={{ borderRadius: 2, minWidth: 180, fontSize: 13 }}
          >
            {TIPOS_RELATORIO.map(t => (
              <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>{t.label}</MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            renderValue={(v) => {
              const l = PERIODOS.find(p => p.value === v)?.label;
              return <Typography sx={{ fontSize: 12.5, color: '#1E293B' }}>Período: <b>{l}</b></Typography>;
            }}
            sx={{ borderRadius: 2, minWidth: 180, fontSize: 13 }}
          >
            {PERIODOS.map(p => (
              <MenuItem key={p.value} value={p.value} sx={{ fontSize: 13 }}>{p.label}</MenuItem>
            ))}
          </Select>

          <Box sx={{ flex: 1 }} />

          {/* título do relatório gerado */}
          <Box sx={{
            px: 2, py: 0.8,
            bgcolor: 'rgba(249,115,22,0.08)',
            border: '1px solid rgba(249,115,22,0.2)',
            borderRadius: 2,
          }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: ORANGE }}>
              {tipoLabel} · {periodoLabel} · {MOCK.length} registros
            </Typography>
          </Box>
        </Box>

        {/* tabela */}
        <TableContainer>
          <Table
            size="small"
            sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F1F5F9' } }}
          >
            <TableHead>
              <TableRow>
                <TH>Usuário</TH>
                <TH>Email</TH>
                <TH>Tipo</TH>
                <TH>Status</TH>
                <TH align="right">Faturamento</TH>
                <TH align="right">Despesas</TH>
                <TH align="right">Saldo</TH>
                <TH align="right">Lançamentos</TH>
              </TableRow>
            </TableHead>
            <TableBody>
              {MOCK.map((row) => {
                const saldo = row.faturamento - row.despesas;
                return (
                  <TableRow
                    key={row.id}
                    sx={{ '&:hover': { bgcolor: 'rgba(249,115,22,0.025)' }, transition: 'background 0.12s' }}
                  >
                    {/* usuário */}
                    <TableCell sx={{ py: 1.6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{
                          width: 32, height: 32, fontSize: 12, fontWeight: 700,
                          background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                        }}>
                          {row.nome.charAt(0)}
                        </Avatar>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B' }}>
                          {row.nome}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>{row.email}</Typography>
                    </TableCell>

                    <TableCell><TipoChip tipo={row.tipo} /></TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: row.status ? GREEN : '#CBD5E1' }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: row.status ? GREEN : '#94A3B8' }}>
                          {row.status ? 'Ativo' : 'Inativo'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>{fmt(row.faturamento)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: RED }}>{fmt(row.despesas)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: saldo >= 0 ? GREEN : RED }}>{fmt(saldo)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        minWidth: 32, height: 22, px: 1, borderRadius: 10,
                        bgcolor: 'rgba(37,99,235,0.08)',
                      }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE }}>{row.lancamentos}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* rodapé */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2,
          borderTop: '1px solid #F1F5F9',
          flexWrap: 'wrap', gap: 1,
        }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {[
              { label: 'Total Faturamento', val: fmt(totalFat),  color: GREEN  },
              { label: 'Total Despesas',    val: fmt(totalDesp), color: RED    },
              { label: 'Saldo Geral',       val: fmt(totalFat - totalDesp), color: totalFat - totalDesp >= 0 ? GREEN : RED },
            ].map((s) => (
              <Box key={s.label}>
                <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
            {MOCK.length} registros · {tipoLabel} · {periodoLabel}
          </Typography>
        </Box>
      </Card>

    </Box>
  );
};

export default RelatoriosAdmin;
